/**
 * تفاوت این بررسی با بررسی قبلی.
 *
 * ⚠️ **چرا این نما لازم شد:** بازخورد کاربر این بود که «۴-۵ بار ابعاد تصمیم را
 * مطابق نظرش عوض کردم ولی باز قبول نکرد، انگار تأثیری روی نظرش نداشتم». بخشی
 * از آن یک نقص واقعی بود و در سرور رفع شد، اما بخش دیگرش نمایشی است: حتی وقتی
 * اصلاح کاربر واقعاً اثر داشته، خروجی قبلی راهی نداشت آن را نشان بدهد. یک حکمِ
 * درستِ ثابت هم باید بگوید چه چیزی بسته شد و چه چیزی باقی است، وگرنه از یک
 * حکمِ بی‌اعتنا قابل تشخیص نیست.
 *
 * مقایسه‌ی موضع در سرور و به‌شکل قطعی انجام می‌شود؛ این نما چیزی را استنباط
 * نمی‌کند.
 */

import { ArrowLeftRight, CircleCheck, CircleSlash, MinusCircle, PlusCircle } from 'lucide-react'

import { Banner, Card, SectionHeader } from '@/components/ui/primitives'
import { fa } from '@/lib/format'
import { verdictHeadline } from '@/lib/labels'
import { useMeta } from '@/lib/meta-context'

import type { ConditionLedger, VerdictTransition } from '@/lib/types'

interface TransitionPanelProps {
  transition: VerdictTransition
  ledger: ConditionLedger
}

export const TransitionPanel = ({ transition, ledger }: TransitionPanelProps) => {
  const { meta } = useMeta()
  // تلاش اول مقایسه‌ای ندارد و نمایش «تغییر» در آن گمراه‌کننده است.
  if (transition.previous_verdict === null || transition.current_verdict === null) {
    return null
  }

  const total = ledger.conditions.length
  // برچسب از کاتالوگ سرور می‌آید، نه از رشته‌ی محلی: نوار حکم و این کارت باید
  // برای یک موضع یک نام بگویند.
  const previousLabel = verdictHeadline(
    meta.labels,
    transition.previous_verdict,
    transition.previous_direction,
  )
  const currentLabel = verdictHeadline(
    meta.labels,
    transition.current_verdict,
    transition.current_direction,
  )

  return (
    <Card>
      <SectionHeader
        title="تغییر نسبت به بررسی قبلی"
        caption={
          transition.changed
            ? `موضع از «${previousLabel}» به «${currentLabel}» رسید.`
            : `موضع همان «${currentLabel}» ماند.`
        }
        icon={<ArrowLeftRight className="size-4" />}
      />
      <div className="space-y-4 p-5">
        {total > 0 && (
          <p className="text-sm leading-relaxed text-ink">
            از {fa(total)} شرط ثبت‌شده، {fa(transition.resolved_conditions.length)} اصلاح
            شده، {fa(transition.remaining_conditions.length)} باقی مانده
            {transition.contradicted_conditions.length > 0 &&
              ` و ${fa(transition.contradicted_conditions.length)} رد شده`}
            .
          </p>
        )}

        {transition.reason !== '' && (
          <p className="rounded-md border border-line bg-surface-sunken p-3 text-sm leading-relaxed text-ink">
            {transition.reason}
          </p>
        )}

        {/*
          «پایدار» یعنی این دور هیچ مبنای تازه‌ای نداشت. گفتنش صریح، کاربر را از
          تکرار بی‌فایده نجات می‌دهد؛ سکوت در این حالت شبیه بی‌اعتنایی بود.
        */}
        {transition.stable && (
          <Banner tone="warning">
            این بررسی مبنای تازه‌ای نسبت به دور قبل نداشت، پس موضع هم عوض نشد. برای
            تغییر نتیجه، یا شرط باقی‌مانده را با سند و عدد اثبات کنید یا ساختار تصمیم
            را عوض کنید؛ تکرار همان توصیف، نتیجه را عوض نمی‌کند.
          </Banner>
        )}

        <ConditionGroup
          title="اصلاح شد"
          items={transition.resolved_conditions}
          icon={<CircleCheck className="size-3.5 text-emerald-600" aria-hidden />}
        />
        <ConditionGroup
          title="باقی مانده"
          items={transition.remaining_conditions}
          icon={<MinusCircle className="size-3.5 text-amber-600" aria-hidden />}
        />
        <ConditionGroup
          title="رد شد"
          items={transition.contradicted_conditions}
          icon={<CircleSlash className="size-3.5 text-rose-600" aria-hidden />}
        />
        <ConditionGroup
          title="مانع تازه"
          items={transition.new_conditions}
          icon={<PlusCircle className="size-3.5 text-ink-muted" aria-hidden />}
        />
      </div>
    </Card>
  )
}

interface ConditionGroupProps {
  title: string
  items: string[]
  icon: React.ReactNode
}

/** یک دسته از شرط‌ها. دسته‌ی خالی رندر نمی‌شود تا فهرست، خالی به نظر نرسد. */
const ConditionGroup = ({ title, items, icon }: ConditionGroupProps) => {
  if (items.length === 0) {
    return null
  }
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-ink-muted">{title}</p>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-sm leading-relaxed text-ink">
            <span className="mt-1 shrink-0">{icon}</span>
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
